import { useEffect, useState } from "react";
import { fetchCarVariantData } from "@/app/product/utils/car.utils";
import { useAppData } from "@/store/appStore";
import { useConfig } from "@/hooks/useConfig";
import { fetchPopularList } from "@/components/homepage/homepageUtils";
import {
	PRODUCT_TYPE_CAR,
	PRODUCT_TYPE_MOBILE,
	PRODUCT_NAME_DEVICES,
} from "@/constants/productTypes";

export const useBottomNavData = () => {
	const [previousSelectedCar, setPreviousSelectedCar] = useState<Car | null>(
		null,
	);
	const [featuredCars, setFeaturedCars] = useState<Car[]>([]);
	const [featuredDevices, setFeaturedDevices] = useState<Car[]>([]);
	const [appData] = useAppData();
	const { isConfigReady } = useConfig();

	// Fetch car variant data when variantId changes
	useEffect(() => {
		const existingSearchValue = appData?.variantId;
		if (existingSearchValue) {
			fetchCarVariantData({
				carVariantId: existingSearchValue,
				setCarVariantData: setPreviousSelectedCar,
			});
		}
	}, [appData?.variantId]);

	// Fetch popular list when config is ready or onboarding type changes
	useEffect(() => {
		const loadFeaturedData = async () => {
			if (!isConfigReady || !appData?.cityId) {
				return;
			}

			try {
				if (appData?.onboardingCompletionType === PRODUCT_NAME_DEVICES) {
					const devices =
						(await fetchPopularList({
							cityId: appData.cityId,
							productType: PRODUCT_TYPE_MOBILE,
							salary: appData?.ctc,
						})) ?? [];
					setFeaturedDevices(devices);
				} else {
					const cars =
						(await fetchPopularList({
							cityId: appData.cityId,
							productType: PRODUCT_TYPE_CAR,
							salary: appData?.ctc,
						})) ?? [];
					setFeaturedCars(cars);
				}
			} catch (error) {
				console.error("Error loading featured data in BottomNav:", error);
			}
		};

		loadFeaturedData();
	}, [
		isConfigReady,
		appData?.cityId,
		appData?.ctc,
		appData?.onboardingCompletionType,
	]);

	// Helper function to get the appropriate product for navigation
	const getNavigationProduct = (): Car | null => {
		const existingSearchValue = appData?.variantId;

		// If there's an existing search value, prefer previously selected product
		if (existingSearchValue && previousSelectedCar) {
			// If previous selection type mismatches current onboarding type, return corresponding featured item
			if (
				appData?.onboardingCompletionType === PRODUCT_NAME_DEVICES &&
				previousSelectedCar.type === PRODUCT_TYPE_CAR
			) {
				return featuredDevices[0] || null;
			}
			if (
				appData?.onboardingCompletionType !== PRODUCT_NAME_DEVICES &&
				previousSelectedCar.type === PRODUCT_TYPE_MOBILE
			) {
				return featuredCars[0] || null;
			}

			return previousSelectedCar;
		}

		// If no existing search value, determine based on onboardingCompletionType
		if (appData?.onboardingCompletionType === PRODUCT_NAME_DEVICES) {
			return featuredDevices[0] || null;
		} else {
			return featuredCars[0] || null;
		}
	};

	const navigationProduct = getNavigationProduct();
	const isReady = isConfigReady && !!navigationProduct;

	return {
		navigationProduct,
		isReady,
	};
};
